import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { SeoCatalogue, SeoContent, SeoMakeCatalogue } from '@/lib/seo';

export interface Seo {
  /** The full document title, brand suffix included. */
  title: string;
  description?: string;
  /** Path only, beginning with a slash. Resolved against the current origin. */
  canonicalPath?: string;
  /** Absolute or root-relative URL of the sharing image. */
  image?: string;
  noIndex?: boolean;
}

/**
 * Keeps the document head in step with client-side navigation.
 *
 * The server writes the correct head for the URL that was requested, and that is what every crawler
 * and every link preview sees. What it cannot do is follow a visitor from the search page to a car
 * and back: those are history pushes, not requests, so without this the tab title, the canonical
 * link and the robots directive all still describe the page the visitor arrived on. The tab title is
 * the visible half of that — someone with eight cars open in eight tabs is reading those titles —
 * and the canonical is the half that matters to a search engine rendering the page.
 *
 * Pass `null` while the data a title depends on is still loading. Writing "undefined — KojaKola"
 * into the title bar for half a second is worse than leaving the previous one there.
 */
export function useSeo(seo: Seo | null): void {
  const { title, description, canonicalPath, image, noIndex } = seo ?? {};

  useEffect(() => {
    if (!title) {
      return;
    }

    document.title = title;

    if (description) {
      setMeta('name', 'description', description);
      setMeta('property', 'og:description', description);
      setMeta('name', 'twitter:description', description);
    }

    setMeta('property', 'og:title', title);
    setMeta('name', 'twitter:title', title);

    setMeta(
      'name',
      'robots',
      noIndex
        ? 'noindex, follow'
        : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
    );

    if (canonicalPath) {
      const absolute = new URL(canonicalPath, window.location.origin).toString();

      setLink('canonical', absolute);
      setMeta('property', 'og:url', absolute);
    }

    if (image) {
      const absolute = new URL(image, window.location.origin).toString();

      setMeta('property', 'og:image', absolute);
      setMeta('name', 'twitter:image', absolute);
      setMeta('name', 'twitter:card', 'summary_large_image');
    }
  }, [title, description, canonicalPath, image, noIndex]);
}

/**
 * Finds the tag the server already wrote and edits it, rather than appending a second one.
 *
 * Two canonical links or two robots directives is not a cosmetic problem: which one wins is
 * unspecified, so the page's own instructions to a search engine become a coin toss.
 */
function setMeta(attribute: 'name' | 'property', key: string, content: string): void {
  const selector = `meta[${attribute}="${key}"]`;

  let element = document.head.querySelector<HTMLMetaElement>(selector);

  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.append(element);
  }

  element.setAttribute('content', content);
}

function setLink(rel: string, href: string): void {
  let element = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);

  if (!element) {
    element = document.createElement('link');
    element.rel = rel;
    document.head.append(element);
  }

  element.href = href;
}

/**
 * The questions and the guide copy, fetched rather than hardcoded here.
 *
 * The server emits the same text as FAQ structured data, and Google requires the answers in that
 * markup to be visible on the page. Keeping one copy on the server and reading it here is what makes
 * that true by construction instead of by everyone remembering to change both.
 */
export function useSeoContent() {
  return useQuery({
    queryKey: ['seo-content'],
    queryFn: () => api<SeoContent>('/api/seo/content'),
    staleTime: Infinity,
    gcTime: Infinity,
  });
}

/** Makes that have cars for sale, with counts. */
export function useSeoCatalogue() {
  return useQuery({
    queryKey: ['seo-catalogue'],
    queryFn: () => api<SeoCatalogue>('/api/seo/catalogue'),
    staleTime: 15 * 60_000,
  });
}

/** One make's models, with counts. Skipped entirely on the category root. */
export function useSeoMakeCatalogue(makeSlug: string | undefined) {
  return useQuery({
    queryKey: ['seo-catalogue', makeSlug],
    queryFn: () => api<SeoMakeCatalogue>(`/api/seo/catalogue/${makeSlug}`),
    enabled: Boolean(makeSlug),
    staleTime: 15 * 60_000,
    retry: false,
  });
}
